<template>
  <SmDialog
    v-model:visibility="visibility"
    :title="i18n.t('export')"
    size="small">
    <template #default>
      <div>
        <div class="mb-6 flex items-center justify-between">
          <span>{{ i18n.t('exportIgnoreUnassignedQuota') }}</span>
          <SmSwitch v-model="ignoreUnassignedQuota" />
        </div>

        <div class="flex items-center justify-between">
          <span>{{ i18n.t('format') }}</span>
          <el-radio-group v-model="format">
            <el-radio-button :label="OutputFormat.CSV" />
            <el-radio-button :label="OutputFormat.JSON" />
            <el-radio-button :label="OutputFormat.XLSX" />
          </el-radio-group>
        </div>
      </div>
    </template>

    <template #footer>
      <SmButton v-if="!loading" outline @click="visibility = false">
        {{ i18n.t('general.cancel') }}
      </SmButton>

      <SmButton :loading="loading" @click="exportData">
        {{ i18n.t('export') }}
      </SmButton>
    </template>
  </SmDialog>
</template>

<script setup lang="ts">
  import dayjs from 'dayjs'
  import { useI18n } from 'vue-i18n'

  import { OutputFormat, OptimizationExportHandlerFieldKeys } from '@/client'
  import { exportOptimizationsResource } from '@/utils/export'
  import { getApiErrorMessage } from '@/utils/errors'
  import { sendToast } from '@/components/sm/SmNotification'
  import { usePreferenceStore } from '@/stores/preferenceStore'

  interface Props {
    applicationId?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    applicationId: undefined,
  })

  const i18n = useI18n()
  const preferenceStore = usePreferenceStore()

  const visibility = defineModel('visibility', {
    type: Boolean,
    default: true,
  })

  const loading = ref(false)
  const format = ref<OutputFormat>(OutputFormat.CSV)
  const ignoreUnassignedQuota = ref<boolean>(false)

  function exportData() {
    loading.value = true

    const queryParams: Record<string, unknown> = {}
    if (props.applicationId) {
      queryParams['references.software_id'] = props.applicationId
    }

    const requestBody = {
      output_format: format.value,
      column_keys_human_readable: true,
      columns: [
        OptimizationExportHandlerFieldKeys._ID,
        OptimizationExportHandlerFieldKeys.SOFTWARE_DISPLAY_NAME,
        OptimizationExportHandlerFieldKeys.TYPE,
        OptimizationExportHandlerFieldKeys.REASON,
        OptimizationExportHandlerFieldKeys.ACCOUNT_EMAIL,
        OptimizationExportHandlerFieldKeys.ACCOUNT_NAME,
        OptimizationExportHandlerFieldKeys.YEARLY_SAVINGS,
        OptimizationExportHandlerFieldKeys.DATA_LAST_LOGIN_TIME,
        OptimizationExportHandlerFieldKeys.DATA_NEVER_LOGGED_IN,
        OptimizationExportHandlerFieldKeys.DATA_SOFTWARE_LICENSES_ASSIGNED_NAMES,
        OptimizationExportHandlerFieldKeys.DATA_SOFTWARE_LICENSE_TO_ADD_NAMES,
        OptimizationExportHandlerFieldKeys.DATA_SOFTWARE_LICENSE_TO_REMOVE_NAMES,
      ],
      cost_config: {
        target_currency_code: preferenceStore.preferences?.currency_code,
        user_cost_ignore_unassigned_quota: ignoreUnassignedQuota.value,
      },
    }

    exportOptimizationsResource({ requestBody, queryParams })
      .then(async (response) => {
        if (response.status !== 200) {
          loading.value = false
          return
        }

        let returnValue: string | Blob
        if (format.value === OutputFormat.JSON) {
          returnValue = JSON.stringify(await response.json(), null, 2)
        } else if (format.value === OutputFormat.XLSX) {
          returnValue = await response.blob()
        } else {
          returnValue = await response.text()
        }

        const blob =
          format.value === OutputFormat.XLSX
            ? (returnValue as Blob)
            : new Blob([returnValue], { type: 'text/plain' })

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const date = dayjs()
        link.setAttribute(
          'download',
          `${date.format('YYYY-MM-DD')}-optimizations.${format.value.toLowerCase()}`
        )
        document.body.appendChild(link)
        link.click()
        link.remove()
        visibility.value = false
        sendToast(
          i18n.t('optimizationsExportedSuccessfully'),
          undefined,
          'success'
        )
      })
      .catch((error) => {
        sendToast(
          getApiErrorMessage(error, i18n as ReturnType<typeof useI18n>),
          undefined,
          'error'
        )
      })
      .finally(() => {
        loading.value = false
      })
  }
</script>
